import { apiPost, apiGet } from './api';

export interface PaymentRequest {
  featureType?: string;
  groupId?: number;
  quantity?: number;
  amount?: number;
  donorEmail?: string;
  eventName?: string | undefined;
  originUrl?: string;
}

export interface PaymentResponse {
  paymentId: string;
  checkoutUrl: string;
  amount: number;
  description: string;
  featureType?: string;
  quantity?: number;
}

// apiGet unwraps the backend envelope (it returns response.data.data), so
// getPaymentStatus resolves to the payment object directly. This type models
// that inner shape — not the { success, data } envelope.
export interface PaymentStatus {
  id: string;
  status: string;
  amount: {
    currency: string;
    value: string;
  };
  description: string;
  isPaid: boolean;
  createdAt: string;
  paidAt?: string;
  // Not returned by the current status endpoint, but read defensively by the
  // success pages (always undefined at runtime); optional for type-safety.
  metadata?: {
    featureType?: string;
    originUrl?: string;
    [key: string]: string | undefined;
  };
}

export class PaymentService {
  
  /**
   * Create premium feature payment
   */
  static async createPremiumPayment(request: PaymentRequest): Promise<PaymentResponse> {
    try {
      const response = await apiPost<PaymentResponse>('/payments/premium', request);
      return response;
    } catch (error: any) {
      console.error('Error creating premium payment:', error);
      throw new Error(error.response?.data?.message || 'Failed to create payment');
    }
  }

  /**
   * Create donation payment
   */
  static async createDonationPayment(request: PaymentRequest): Promise<PaymentResponse> {
    try {
      const response = await apiPost<PaymentResponse>('/payments/donation', request);
      return response;
    } catch (error: any) {
      console.error('Error creating donation payment:', error);
      throw new Error(error.response?.data?.message || 'Failed to create donation payment');
    }
  }

  /**
   * Get payment status
   */
  static async getPaymentStatus(paymentId: string): Promise<PaymentStatus> {
    try {
      const response = await apiGet<PaymentStatus>(`/payments/status/${paymentId}`);
      return response;
    } catch (error: any) {
      console.error('Error getting payment status:', error);
      throw new Error(error.response?.data?.message || 'Failed to get payment status');
    }
  }

  /**
   * Redirect to Mollie iDEAL checkout
   */
  static redirectToCheckout(checkoutUrl: string): void {
    window.location.href = checkoutUrl;
  }

  /**
   * Check if payment is completed (polling function)
   */
  static async waitForPaymentCompletion(
    paymentId: string, 
    maxAttempts: number = 60, 
    intervalMs: number = 2000
  ): Promise<boolean> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const status = await this.getPaymentStatus(paymentId);

        if (status?.isPaid) {
          return true;
        }

        if (status?.status === 'failed' || status?.status === 'expired') {
          return false;
        }

        // Wait before next check
        await new Promise(resolve => setTimeout(resolve, intervalMs));
        
      } catch (error) {
        console.warn('Error checking payment status:', error);
        // Continue checking despite errors
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    
    return false; // Timeout reached
  }

  /**
   * Format amount for display
   */
  static formatAmount(amount: number): string {
    return `€${amount.toFixed(2)}`;
  }

  /**
   * Get payment description based on feature type
   */
  static getPaymentDescription(featureType: string, quantity: number = 1): string {
    switch (featureType) {
      case 'EXTRA_GROUP_SLOTS':
        return `Extra groep plekken (+${quantity * 3})`;
      case 'EXTENDED_GROUP_SIZE':
        return `Groep uitbreiding (+${quantity * 5} plekken)`;
      case 'CHAT_ENHANCEMENTS':
        return 'Chat verbetering (GIF berichten)';
      default:
        return 'Premium upgrade';
    }
  }

  /**
   * Handle payment success - redirect to appropriate page
   */
  static handlePaymentSuccess(featureType?: string, paymentId?: string): void {
    const params = new URLSearchParams();
    if (paymentId) params.set('payment', paymentId);
    
    let redirectPath = '/premium/success';
    
    if (featureType) {
      switch (featureType) {
        case 'EXTRA_GROUP_SLOTS':
          redirectPath = '/groups?payment=success';
          break;
        case 'EXTENDED_GROUP_SIZE':
          redirectPath = '/groups?payment=success';
          break;
        case 'CHAT_ENHANCEMENTS':
          redirectPath = '/premium/success';
          break;
        default:
          redirectPath = '/premium/success';
      }
    }

    const url = params.toString() ? `${redirectPath}?${params.toString()}` : redirectPath;
    window.location.href = url;
  }

  /**
   * Handle payment cancellation or failure
   */
  static handlePaymentFailure(reason: string = 'Payment was cancelled'): void {
    // You can redirect to a failure page or show a message
    console.warn('Payment failed:', reason);
    // For now, we'll stay on the current page and let the component handle it
  }
}

export default PaymentService;